/**
 * weatherIconMapper.ts
 * Maps TAF/METAR weather conditions to visual icon descriptors.
 * Used by ForecastTimeline to display a distinct icon per hourly card.
 */

export type WeatherIconName =
  | 'thunderstorm' |'freezingRain' |'snow' |'heavyRain' |'showers' |'lightRain' |'drizzle' |'fog' |'mist' |'overcast' |'brokenClouds' |'partlyCloudy' |'mostlySunny' |'clear' |'unknown';

export type WeatherSeverity = 'danger' | 'caution' | 'advisory' | 'normal' | 'good';

export interface WeatherIconDescriptor {
  iconName: WeatherIconName;
  label: string;
  severity: WeatherSeverity;
  colorClass: string;
}

export interface WeatherConditions {
  /** Raw TAF/METAR segment text (used for code matching) */
  rawSegment?: string;
  /** Decoded weather string (e.g. "Pluie faible", "Averses de pluie") */
  weatherDecoded?: string;
  /** Raw weather code from TAF/METAR (e.g. "-RA", "TSRA", "BR") */
  weatherCode?: string;
  /** Cloud layer code: CLR, FEW, SCT, BKN, OVC, VV */
  ceilingLayer?: string;
  /** Ceiling in feet */
  ceilingFt?: number;
  /** Visibility string (e.g. ">6 SM", "1 SM", "3000 m") */
  visibility?: string;
  /** Flight category */
  category?: 'VFR' | 'MVFR' | 'IFR' | 'LIFR';
  /** Whether a TEMPO period is active */
  tempoActive?: boolean;
  /** TEMPO weather code */
  tempoWeatherCode?: string;
  /** TEMPO decoded weather */
  tempoWeatherDecoded?: string;
  /** TEMPO ceiling ft */
  tempoCeilingFt?: number;
  /** TEMPO ceiling layer */
  tempoCeilingLayer?: string;
  /** TEMPO visibility */
  tempoVisibility?: string;
  /** PROB period active */
  probActive?: boolean;
  /** PROB weather code */
  probWeatherCode?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function visibilityToSm(vis?: string): number {
  if (!vis) return 10;
  if (vis.startsWith('>') || vis.startsWith('Plus de')) return 10;
  const smMatch = vis.match(/(\d+(?:\.\d+)?)\s*SM/i);
  if (smMatch) return Number(smMatch[1]);
  const kmMatch = vis.match(/(\d+(?:\.\d+)?)\s*km/i);
  if (kmMatch) return Number(kmMatch[1]) * 0.621371;
  const mMatch = vis.match(/(\d+)\s*m/i);
  if (mMatch) return Number(mMatch[1]) / 1609.34;
  return 10;
}

/** Test if a raw segment or decoded string contains a weather pattern */
function hasPattern(text: string | undefined, pattern: RegExp): boolean {
  if (!text) return false;
  return pattern.test(text);
}

// ─── Core mapping logic ───────────────────────────────────────────────────────

function mapConditionsToIcon(
  rawSegment: string | undefined,
  weatherCode: string | undefined,
  weatherDecoded: string | undefined,
  ceilingLayer: string | undefined,
  ceilingFt: number | undefined,
  visibility: string | undefined
): WeatherIconDescriptor {
  const raw = (rawSegment ?? '') + ' ' + (weatherCode ?? '') + ' ' + (weatherDecoded ?? '');
  const visSm = visibilityToSm(visibility);
  const ceil = ceilingFt ?? 99999;

  // ── Priority 1: Dangerous phenomena ──────────────────────────────────────

  // Thunderstorm
  if (hasPattern(raw, /\bTS(RA|SN|GR|GS|UP|PL)?\b/) ||
      hasPattern(raw, /Orage/i) ||
      hasPattern(raw, /TSTM/i)) {
    return {
      iconName: 'thunderstorm',
      label: 'Orage',
      severity: 'danger',
      colorClass: 'text-purple-700',
    };
  }

  // Freezing precipitation
  if (hasPattern(raw, /\bFZRA\b/) ||
      hasPattern(raw, /\bFZDZ\b/) ||
      hasPattern(raw, /\bPL\b/) ||
      hasPattern(raw, /verglaçante/i)) {
    return {
      iconName: 'freezingRain',
      label: 'Pluie verglaçante',
      severity: 'danger',
      colorClass: 'text-red-600',
    };
  }

  // Snow
  if (hasPattern(raw, /\b(\+|-)?SN\b/) ||
      hasPattern(raw, /\bSHSN\b/) ||
      hasPattern(raw, /Neige/i)) {
    return {
      iconName: 'snow',
      label: 'Neige',
      severity: 'caution',
      colorClass: 'text-sky-400',
    };
  }

  // Dense fog / very low visibility
  if ((hasPattern(raw, /\bFG\b/) && visSm < 1) ||
      hasPattern(raw, /\bBCFG\b/) ||
      hasPattern(raw, /\bMIFG\b/) ||
      hasPattern(raw, /Brouillard/i) ||
      visSm < 1) {
    return {
      iconName: 'fog',
      label: 'Brouillard',
      severity: 'danger',
      colorClass: 'text-slate-500',
    };
  }

  // ── Priority 2: Precipitation ─────────────────────────────────────────────

  // Heavy rain
  if (hasPattern(raw, /\+RA\b/) ||
      hasPattern(raw, /Pluie forte/i) ||
      hasPattern(raw, /Fort orage/i)) {
    return {
      iconName: 'heavyRain',
      label: 'Pluie forte',
      severity: 'caution',
      colorClass: 'text-blue-800',
    };
  }

  // Showers
  if (hasPattern(raw, /\bSHRA\b/) ||
      hasPattern(raw, /Averses/i)) {
    return {
      iconName: 'showers',
      label: 'Averses',
      severity: 'caution',
      colorClass: 'text-blue-600',
    };
  }

  // Light / moderate rain
  if (hasPattern(raw, /\b-?RA\b/) ||
      hasPattern(raw, /Pluie/i)) {
    const isLight = hasPattern(raw, /\b-RA\b/) || hasPattern(raw, /Pluie faible/i);
    return {
      iconName: 'lightRain',
      label: isLight ? 'Pluie faible' : 'Pluie',
      severity: 'advisory',
      colorClass: 'text-blue-500',
    };
  }

  // Drizzle
  if (hasPattern(raw, /\bDZ\b/) ||
      hasPattern(raw, /Bruine/i)) {
    return {
      iconName: 'drizzle',
      label: 'Bruine',
      severity: 'advisory',
      colorClass: 'text-sky-500',
    };
  }

  // ── Priority 3: Visibility ────────────────────────────────────────────────

  // Mist / haze / low visibility (1–3 SM)
  if (hasPattern(raw, /\bBR\b/) ||
      hasPattern(raw, /\bHZ\b/) ||
      hasPattern(raw, /\bFG\b/) ||
      hasPattern(raw, /Brume/i) ||
      hasPattern(raw, /Brume sèche/i) ||
      (visSm >= 1 && visSm < 3)) {
    return {
      iconName: 'mist',
      label: 'Brume',
      severity: 'advisory',
      colorClass: 'text-slate-400',
    };
  }

  // ── Priority 4: Cloud cover ───────────────────────────────────────────────

  const layer = ceilingLayer ?? 'CLR';

  if (layer === 'OVC' || layer === 'VV' || ceil < 3000) {
    return {
      iconName: 'overcast',
      label: 'Couvert',
      severity: 'normal',
      colorClass: 'text-slate-600',
    };
  }

  if (layer === 'BKN') {
    return {
      iconName: 'brokenClouds',
      label: 'Fragmenté',
      severity: 'normal',
      colorClass: 'text-slate-500',
    };
  }

  if (layer === 'SCT') {
    return {
      iconName: 'partlyCloudy',
      label: 'Nuages épars',
      severity: 'good',
      colorClass: 'text-slate-400',
    };
  }

  if (layer === 'FEW') {
    return {
      iconName: 'mostlySunny',
      label: 'Quelques nuages',
      severity: 'good',
      colorClass: 'text-amber-400',
    };
  }

  if (layer === 'CLR' || layer === 'SKC' || layer === 'NSC' || ceil >= 99999) {
    return {
      iconName: 'clear',
      label: 'Clair',
      severity: 'good',
      colorClass: 'text-amber-500',
    };
  }

  // ── Priority 5: Default ───────────────────────────────────────────────────
  return {
    iconName: 'unknown',
    label: 'Inconnu',
    severity: 'normal',
    colorClass: 'text-slate-400',
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the best weather icon descriptor for a given set of conditions.
 * TEMPO/PROB conditions are considered when active and more severe than base.
 */
export function getWeatherIconForConditions(conditions: WeatherConditions): WeatherIconDescriptor {
  // Determine base icon
  const baseIcon = mapConditionsToIcon(
    conditions.rawSegment,
    conditions.weatherCode,
    conditions.weatherDecoded,
    conditions.ceilingLayer,
    conditions.ceilingFt,
    conditions.visibility
  );

  // If TEMPO is active, compute TEMPO icon and pick the worse one
  if (conditions.tempoActive) {
    const tempoIcon = mapConditionsToIcon(
      conditions.rawSegment,
      conditions.tempoWeatherCode,
      conditions.tempoWeatherDecoded,
      conditions.tempoCeilingLayer,
      conditions.tempoCeilingFt,
      conditions.tempoVisibility
    );

    const severityRank: Record<WeatherSeverity, number> = {
      good: 0,
      normal: 1,
      advisory: 2,
      caution: 3,
      danger: 4,
    };

    // Use TEMPO icon if it's more severe than base
    if (severityRank[tempoIcon.severity] > severityRank[baseIcon.severity]) {
      return {
        ...tempoIcon,
        label: `TEMPO ${tempoIcon.label}`,
      };
    }
  }

  return baseIcon;
}
